"use client";
import {
  getVehicleTypeList,
  useGetCityByName,
  useGetCountries,
  useGetStatesByName,
} from "@/hooks/useCommon";
import { usePackageModeMappingStore } from "@/store/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, Plus, RefreshCcw } from "lucide-react";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

const packageMappingSearchSchema = z.object({
  city: z.string().optional(),
  country_id: z.string().optional(),
  booking_type: z.string().optional(),
  booking_type_id: z.string().optional(),
  vehicle_type: z.string().optional(),
  vehicle_type_id: z.string().optional(),
  state: z.string().optional(),
  state_id: z.string().optional(),
  city_id: z.string().optional(),
});

type PackageMappingSearchData = z.infer<typeof packageMappingSearchSchema>;

type Option = {
  label: string;
  value: number;
};

const bookingTypes = [
  {
    id: 1,
    booking_type_id: 1,
    name: "Rental",
  },
  {
    id: 2,
    booking_type_id: 1,
    name: "City Taxi",
  },
  {
    id: 3,
    booking_type_id: 1,
    name: "Airport Transfer",
  },
  {
    id: 4,
    booking_type_id: 1,
    name: "Outstation",
  },
  {
    id: 5,
    booking_type_id: 1,
    name: "One Way",
  },
  {
    id: 6,
    booking_type_id: 6,
    name: "Activity",
  },
  {
    id: 7,
    booking_type_id: 1,
    name: "Transport Package",
  },
];

export default function PackageModeMappingForm() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PackageMappingSearchData>({
    resolver: zodResolver(packageMappingSearchSchema),
  });

  const router = useRouter();
  const { packageModeData, setPackageModeData, resetPackageModeData } =
    usePackageModeMappingStore();

  const { mutate: StateCountByStateName, data: States } = useGetStatesByName();
  const { mutate: CityCountByName, data: City } = useGetCityByName();

  const watchState = watch("state");
  const watchCityId = watch("city_id");
  const watchCity = watch("city");
  const watchVehicleTypeId = watch("vehicle_type");
  const watchStateId = watch("state_id");

  const [showState, setShowState] = useState<boolean>(false);
  const [showCity, setShowCity] = useState<boolean>(false);

  const [mappingTypeToggle, setMappingTypeToggle] = useState<string>("country");

  const handleReset = () => {
    setValue("country_id", "");
    setValue("booking_type", "");
    setValue("vehicle_type", "");
    setValue("state", "");
    setValue("state_id", "");
    setValue("city", "");
    setValue("city_id", "");
  };

  const { data: countriesList } = useGetCountries();
  const { data: vehicleTypeList, refetch: vehicleTypeRefetch } =
    getVehicleTypeList();

  const countryOptions: Option[] = countriesList?.map((country: any) => ({
    label: country.name,
    value: String(country.id),
  }));

  const handleMappingTypeToggle = (opt: any) => {
    handleReset()
    setMappingTypeToggle((prev) => (prev === opt.id ? "country" : opt.id));
  };

  const onSubmit = (data: PackageMappingSearchData) => {
    setPackageModeData({
      ...data,
      vehicle_type: watchVehicleTypeId,
      city_id: watchCityId,
    });
  };


  useEffect(() => {
    if (mappingTypeToggle === "state") {
      StateCountByStateName({ state_name: watchState });
    }
  }, [watchState]);

  useEffect(() => {
    if (mappingTypeToggle === "city") {
      CityCountByName({ cityname: watchCity });
    }
  }, [watchCity]);

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="add-hotel-form bg-white shadow-sm border border-gray-300 my-3 mt-0 rounded-sm sm:mb-3 sm:mt-0 m-3 lg:m-6"
      >
        <h1 className="bg-gray-300 py-2 px-5 font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-6 h-6 text-blue-400" /> Mapping Search
        </h1>

        <div className="grid grid-cols-2 py-1 pt-6 px-6 sm:grid-cols-3 md:flex md:items-center md:gap-8">
          {[
            { id: "country", label: "Country" },
            { id: "state", label: "State" },
            { id: "city", label: "City" },
          ].map((opt) => (
            <div
              key={opt.id}
              className="flex gap-1 items-center text-sm sm:gap-2"
            >
              <div
                onClick={() => {
                  handleMappingTypeToggle(opt as any);
                }}
                className={`rounded-full border-2 border-gray-500 cursor-pointer bg-white flex items-center px-0.5 transition-all duration-300 ${
                  mappingTypeToggle === opt.id ? "justify-end" : "justify-start"
                } min-w-8 min-h-4 sm:min-w-12 sm:min-h-6`}
              >
                <div className="h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full bg-gray-500 shadow-md transition-all duration-300" />
              </div>
              <p className="text-[10px] font-[400]">{opt.label}</p>
            </div>
          ))}
        </div>

        {Object.keys(errors).length > 0 && (
          <pre className="text-red-600 text-xs">
            {JSON.stringify(errors, null, 2)}
          </pre>
        )}

        <div className="grid grid-cols-1 p-3 sm:p-6 gap-3 sm:grid-cols-2">
          {mappingTypeToggle === "country" && (
            <div className="flex flex-col gap-1">
              <label htmlFor="country" className="font-[500] text-[12px]">
                Country
              </label>

              <select
                {...register("country_id")}
                // value={formData.country}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const selectedOption = countryOptions?.find(
                    (option: any) => option.value === selectedValue
                  );

                  setValue("country_id", selectedValue);
                }}
                className="w-full border text-[12px] border-gray-300 focus:outline-none py-1 px-2 rounded-sm"
              >
                <option value="">Select Country</option>
                {countryOptions?.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mappingTypeToggle === "state" && (
            <div className="flex flex-col gap-1 relative">
              <label htmlFor="state" className="font-[500] text-[12px]">
                State
              </label>

              <input
                {...register("state")}
                type="text"
                placeholder="Enter State"
                className="w-full border text-[12px] border-gray-300 focus:outline-none py-1 px-2 rounded-sm"
                onFocus={() => setShowState(true)}
                onBlur={() => {
                  // Delay hiding dropdown so onMouseDown in option can run first
                  setTimeout(() => setShowState(false), 100);
                }}
              />

              {States && States?.length > 0 && (
                <div className="absolute z-10 top-[50px] w-full bg-white border border-gray-300 max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none text-[12px]">
                  {States.map((state: any) => (
                    <div
                      key={state.state_id}
                      className="cursor-pointer hover:bg-gray-100 px-4 py-1"
                      onMouseDown={() => {
                        setValue("state_id", String(state.state_id));
                        setValue(
                          "state",
                          `${state.state_name} (${state.country_code})`
                        );
                        setShowState(false);
                      }}
                    >
                      {state.state_name} ({state.country_code})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {mappingTypeToggle === "city" && (
            <div className="flex flex-col gap-1 relative">
              <label htmlFor="state" className="font-[500] text-[12px]">
                City
              </label>

              <input
                {...register("city")}
                type="text"
                placeholder="Enter City"
                className="w-full border text-[12px] border-gray-300 focus:outline-none py-1 px-2 rounded-sm"
                onFocus={() => setShowCity(true)}
                onBlur={() => {
                  setTimeout(() => setShowCity(false), 100);
                }}
              />

              {showCity && City && City?.length > 0 && (
                <div className="absolute z-10 top-[50px] w-full bg-white border border-gray-300 max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none text-[12px]">
                  {City.map((city: any) => (
                    <div
                      key={city.city_id}
                      className="cursor-pointer hover:bg-gray-100 px-4 py-1"
                      onMouseDown={() => {
                        setValue("city_id", String(city.city_id));
                        setValue("city", `${city.citystatecountry}`);
                        setShowCity(false);
                      }}
                    >
                      {city.citystatecountry}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label htmlFor="booking_type" className="font-[500] text-[12px]">
              Booking Type
            </label>
            <select
              {...register("booking_type")}
              name="booking_type"
              id=""
              className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px]"
            >
              <option value="">Select Booking Type</option>
              {bookingTypes.map((item: any) => (
                <option
                  // onClick={() => setValue("booking_type_id", item.id)}
                  value={String(item.id)}
                  key={item.id}
                >
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="vehicle_type"
              className="font-[500] text-[12px] text-sm"
            >
              Vehicle Type
            </label>
            <select
              {...register("vehicle_type")}
              onFocus={() => vehicleTypeRefetch()}
              name="vehicle_type"
              className="w-full border text-[12px] border-gray-300 focus:outline-none py-1 px-3 rounded-sm"
            >
              <option value="">Select Vehicle Type</option>
              {vehicleTypeList?.map((type: any) => (
                <option onClick={() => setValue("vehicle_type_id",type.id)} value={type.id} key={type.id} className="text-[12px]">
                  {type.vehicle_type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-2 justify-end p-4 pt-0">
          <button
            onClick={handleReset}
            type="button"
            className="text-[12px] cursor-pointer py-1 px-3 rounded-sm bg-[#E08E0B] text-white"
          >
            Clear
          </button>
          <button
            // onClick={() => alert("Submitting")}
            type="submit"
            className="text-[12px] cursor-pointer py-1 px-3 rounded-sm bg-[#E08E0B] text-white"
          >
            Search
          </button>
        </div>

        <div className="flex items-center justify-end gap-1 px-4 py-3">
          <button
            type="button"
            onClick={() => router.push("/add-booking-type-mapping")}
            className="flex cursor-pointer items-center gap-1 text-[10px] py-2 px-2 rounded-sm bg-[#367FA9] text-white font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Mapping
          </button>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 text-[10px] py-2 px-2 rounded-sm bg-[#DD4B39] text-white font-semibold"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 text-[10px] py-2 px-2 rounded-sm bg-[#00A65A] text-white font-semibold"
          >
            <RefreshCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      </form>
    </>
  );
}
